import * as React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Badge from '@mui/material/Badge';
import {
	AccountCircle,
	Login,
	PersonAdd,
	Settings,
	Logout,
	Verified,
	AdminPanelSettings,
	Favorite,
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useFavorites } from '../../../contexts/FavoritesContext';
import logoImage from '../../../4inABedStays.png';

const pages = [
	{ label: 'Home', path: '/' },
	{ label: 'Properties', path: '/list' },
	{ label: 'About', path: '/about' },
];

const Header = () => {
	const navigate = useNavigate();
	const { user, isAuthenticated, logout, isAdmin, isEmailVerified } = useAuth();
	const { getFavoritesCount } = useFavorites();
	
	const [anchorElNav, setAnchorElNav] = React.useState(null);
	const [anchorElUser, setAnchorElUser] = React.useState(null);

	const handleOpenNavMenu = (event) => {
		setAnchorElNav(event.currentTarget);
	};

	const handleOpenUserMenu = (event) => {
		setAnchorElUser(event.currentTarget);
	};

	const handleCloseNavMenu = () => {
		setAnchorElNav(null);
	};

	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};

	const handleNavigation = (path) => {
		handleCloseNavMenu();
		handleCloseUserMenu();
		navigate(path);
	};

	const handleLogout = async () => {
		handleCloseUserMenu();
		try {
			await logout();
			navigate('/');
		} catch (error) {
			console.error('Logout error:', error);
		}
	};

	// User avatar with initials or default icon
	const renderUserAvatar = () => {
		if (user && user.firstName && user.lastName) {
			const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
			return (
				<Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
					{initials}
				</Avatar>
			);
		}
		return (
			<Avatar sx={{ width: 32, height: 32 }}>
				<AccountCircle />
			</Avatar>
		);
	};

	// User menu items for authenticated users
	const authenticatedMenuItems = [
		{
			label: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'User',
			isHeader: true,
		},
		{ divider: true },
		{
			label: 'Profile',
			icon: <Settings fontSize="small" />,
			onClick: () => handleNavigation('/profile'),
		},
		{
			label: `Favorites (${getFavoritesCount()})`,
			icon: <Favorite fontSize="small" />,
			onClick: () => handleNavigation('/favorites'),
		},
		// Add admin menu item if user is admin
		...(isAdmin() ? [{
			label: 'Admin Dashboard',
			icon: <AdminPanelSettings fontSize="small" />,
			onClick: () => handleNavigation('/admin'),
		}] : []),
		{ divider: true },
		{
			label: 'Logout',
			icon: <Logout fontSize="small" />,
			onClick: handleLogout,
		},
	];

	// Guest menu items
	const guestMenuItems = [
		{
			label: 'Sign In',
			icon: <Login fontSize="small" />,
			onClick: () => handleNavigation('/login'),
		},
		{
			label: 'Sign Up',
			icon: <PersonAdd fontSize="small" />,
			onClick: () => handleNavigation('/register'),
		},
	];

	const menuItems = isAuthenticated ? authenticatedMenuItems : guestMenuItems;

	return (
		<AppBar position="static" elevation={0}>
			<Toolbar>
				{/* Logo - Desktop */}
				<Typography
					variant="h6"
					noWrap
					component={RouterLink}
					to="/"
					sx={{
						mr: 2,
						display: { xs: 'none', md: 'flex' },
						alignItems: 'center',
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<img src={logoImage} alt="4InBedStays Logo" style={{ height: '40px', marginRight: '10px' }} />
				</Typography>

				{/* Mobile Navigation Menu */}
				<Box
					sx={{
						flexGrow: 1,
						display: { xs: 'flex', md: 'none' },
					}}
				>
					<IconButton
						size="large"
						aria-label="navigation menu"
						aria-controls="menu-appbar-mobile"
						aria-haspopup="true"
						onClick={handleOpenNavMenu}
						color="inherit"
					>
						<MenuIcon />
					</IconButton>
					<Menu
						id="menu-appbar-mobile"
						anchorEl={anchorElNav}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left',
						}}
						keepMounted
						transformOrigin={{
							vertical: 'top',
							horizontal: 'left',
						}}
						open={Boolean(anchorElNav)}
						onClose={handleCloseNavMenu}
						sx={{
							display: { xs: 'block', md: 'none' },
						}}
					>
						{pages.map((page) => (
							<MenuItem key={page.label} onClick={() => handleNavigation(page.path)}>
								<Typography textAlign="center">{page.label}</Typography>
							</MenuItem>
						))}
					</Menu>
				</Box>

				{/* Logo - Mobile */}
				<Typography
					variant="h5"
					noWrap
					component={RouterLink}
					to="/"
					sx={{
						mr: 2,
						display: { xs: 'flex', md: 'none' },
						flexGrow: 1,
						alignItems: 'center',
						textDecoration: 'none',
						color: 'inherit',
					}}
				>
					<img src={logoImage} alt="4InBedStays Logo" style={{ height: '30px' }} />
				</Typography>
				
				{/* Navigation Links - Desktop */}
				<Box
					sx={{
						flexGrow: 1,
						display: { xs: 'none', md: 'flex' },
						justifyContent: 'flex-end',
						mr: 2,
					}}
				>
					{pages.map((page) => (
						<Button
							key={page.label}
							component={RouterLink}
							to={page.path}
							sx={{ my: 2, color: 'white', display: 'block', textTransform: 'none' }}
						>
							{page.label}
						</Button>
					))}
				</Box>

				{/* User Menu */}
				<Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
					{/* Favorites Button for Authenticated Users - Desktop */}
					{isAuthenticated && (
						<Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', mr: 1 }}>
							<Tooltip title="Your Favorites">
								<IconButton
									component={RouterLink}
									to="/favorites"
									color="inherit"
									size="large"
									sx={{ 
										p: 1,
										'& .MuiBadge-badge': {
											fontSize: '0.75rem',
											height: '16px',
											minWidth: '16px',
											padding: '0 4px'
										}
									}}
								>
									<Badge badgeContent={getFavoritesCount()} color="error">
										<Favorite />
									</Badge>
								</IconButton>
							</Tooltip>
						</Box>
					)}

					{/* Desktop Auth Buttons for Guests */}
					{!isAuthenticated && (
						<Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
							<Button
								component={RouterLink}
								to="/login"
								color="inherit"
								sx={{ textTransform: 'none' }}
							>
								Sign In
							</Button>
							<Button
								component={RouterLink}
								to="/register"
								variant="outlined"
								sx={{ 
									color: 'white', 
									borderColor: 'white',
									textTransform: 'none',
									'&:hover': {
										borderColor: 'white',
										backgroundColor: 'rgba(255, 255, 255, 0.1)',
									}
								}}
							>
								Sign Up
							</Button>
						</Box>
					)}

					{/* User Avatar Button */}
					<Tooltip title={isAuthenticated ? "Account settings" : "Account options"}>
						<IconButton
							onClick={handleOpenUserMenu}
							sx={{ p: 0, ml: isAuthenticated ? 0 : { xs: 0, md: 2 } }}
						>
							{renderUserAvatar()}
							{/* Email verification indicator */}
							{isAuthenticated && isEmailVerified() && (
								<Verified 
									sx={{ 
										position: 'absolute', 
										bottom: -2, 
										right: -2, 
										fontSize: 14, 
										color: 'success.main',
										backgroundColor: 'white',
										borderRadius: '50%',
									}} 
								/>
							)}
						</IconButton>
					</Tooltip>

					{/* User Menu Dropdown */}
					<Menu
						sx={{ mt: '45px' }}
						id="menu-appbar-user"
						anchorEl={anchorElUser}
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						keepMounted
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						open={Boolean(anchorElUser)}
						onClose={handleCloseUserMenu}
					>
						{menuItems.map((item, index) => {
							if (item.divider) {
								return <Divider key={`divider-${index}`} />;
							}
							
							if (item.isHeader) {
								return (
									<MenuItem key={item.label} disabled>
										<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
											{item.label}
										</Typography>
									</MenuItem>
								);
							}
							
							return (
								<MenuItem key={item.label} onClick={item.onClick}>
									{item.icon && (
										<ListItemIcon>
											{item.icon}
										</ListItemIcon>
									)}
									<Typography textAlign="center">{item.label}</Typography>
								</MenuItem>
							);
						})}
					</Menu>
				</Box>
			</Toolbar>
		</AppBar>
	);
};

export default Header;
